/* eslint-disable camelcase */
import * as Pioneer from "pioneer";
import {
  Cameras,
  SceneHelpers,
  Transitions,
  Placemarks,
} from "pioneer-scripts";
import { setColorRectStyle } from "../components/color_bar";
import {
  getNearestDataIndex,
  setPrecipitationColorRectStyle,
} from "../components/color_bar_html";
import { setReadoutData } from "../components/data_readout";
import { formatNumber } from "../helpers/tools";
import {
  datasetStore,
  datareadStore,
  appStore,
  lightStore,
  poiStore,
  gmapStore,
  stellarStore,
} from "./globalState";
import globalRefs from "./globalRefs";
import { hideLoading, showLoading } from "../components/loading";
import { SPACECRAFT_GEOSTATIONARY } from "../data/spacecraft_data_ar";
import ENTITY_INFO from "../data/entity_info.json";
/**
 * A default function that lerps between positions and slerps between orientations.
 * @param {Entity} entity
 * @param {Vector3} initialPosition
 * @param {Vector3} finalPosition
 * @param {Quaternion} initialOrientation
 * @param {Quaternion} finalOrientation
 * @param {number} u
 */
const easeTransition = (
  entity,
  initialPosition,
  finalPosition,
  initialOrientation,
  finalOrientation,
  u,
) => {
  const position = Pioneer.Vector3.pool.get();
  const orientation = Pioneer.Quaternion.pool.get();

  u = Transitions.easeInOut(u);

  position.lerp(initialPosition, finalPosition, u);
  orientation.slerp(initialOrientation, finalOrientation, u);

  // Set the new position and orientation.
  entity.setPosition(position);
  entity.setOrientation(orientation);
  Pioneer.Vector3.pool.release(position);
  Pioneer.Quaternion.pool.release(orientation);
};

const isInterruptedTransitionError = (error) =>
  error instanceof Pioneer.CanceledError ||
  (error instanceof Error &&
    error.message === "Transition controller was destroyed before completing.");

import ar from "../languages/ar";
import en from "../languages/en";
import { read } from "@popperjs/core";

const collectedData = [];

class CameraManager {
  constructor(pioneer) {
    // Set pioneer and scene.
    this._pioneer = pioneer;
    this._scene = pioneer.getScene("main");

    /**
     * @type {Pioneer.Entity}
     * @private
     */
    this._cameraEntity = this._scene.getEntity("camera");

    /**
     * The entities that are unselectable.
     * @type {string[]}
     * @private
     */
    this._unselectableEntities = [];
    this._previousEntity = null;
    this._currentTarget = null;
    this._isCelsius = false;
    this._transitionComplete = false;

    this.setPickController = this.setPickController.bind(this);
  }

  setIsCelcius(isCelsius) {
    this._isCelsius = isCelsius === 0;
  }

  setIsCelcius(isCelsius) {
    this._isCelsius = isCelsius === 0;
  }

  init() {
    // Set unselectable entities.
    this.setUnselectableEntities(["sun", "earth", "moon"]);

    // Get starting camera position
    this.startingCamPos = new Pioneer.Vector3();
    this._scene
      .getEntity("earth")
      .waitTillInPlace()
      .then(() => {
        this._cameraEntity.getPositionRelativeToEntity(
          this.startingCamPos,
          Pioneer.Vector3.Zero,
          this._scene.getEntity("earth"),
        );
      });
  }

  /**
   * Sets which entities are unselectable.
   * @param {string[]} list
   */
  setUnselectableEntities(list) {
    this._unselectableEntities = list;
  }

  /**
   * Flies the camera to the entity with the given options, and then adds a select controller.
   * @param {string} name
   */
  async goToEntity(name, param) {
    // Check that the entity is not undefined or we are not already going to the entity.
    if (name === undefined || this._currentTarget === name) {
      return;
    }

    this._currentTarget = name;
    const _e = ENTITY_INFO[name];

    // Save the previous entity.
    this._previousEntity = this._cameraEntity.getParent();

    // Unset the forced midDistance setting of the camera.
    this._cameraEntity
      .getComponent(Pioneer.CameraComponent)
      .setMidDistance(undefined);

    // Check if the target has changed while loading
    if (this._currentTarget !== name) {
      return;
    }

    // Use different goTo functions depending on the type of entity.
    try {
      // console.log("CameraManager::goToEntity", name, _e)
      if (name === "earth") {
        await this._goToEarth(param);
      } else if (name.startsWith("sc_")) {
        await this._goToSpacecraft(name);
      }
      //TODO change this to use entity stellar
      else if (_e && _e.stellar) {
        await this._goToPlanet(param, name);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      // Reset the current target.
      this._currentTarget = null;
    }
  }

  /**
   * Goes to a spacecraft and its comparison.
   * @param {Entity} spacecraftEntity
   * @param {Entity} compareEntity
   */
  async goToComparison(spacecraftEntity, compareEntity) {
    const distance =
      Cameras.getDistanceToFitEntities(
        this._cameraEntity,
        this._cameraEntity.getOrientation(),
        spacecraftEntity,
        [compareEntity],
      ) * 1.75;

    await Cameras.goToEntity(this._cameraEntity, spacecraftEntity, {
      distance,
      fixedToParent: true,
      orbiter: true,
      transitionFunction: easeTransition,
    });

    this._cameraEntity
      .getController(Pioneer.OrbitController)
      .setPitchAngleLimits(
        new Pioneer.Interval(-Math.PI / 2 + 0.01, Math.PI / 2 - 0.01),
      );

    const zoomController = this._cameraEntity.getController(
      Pioneer.ZoomController,
    );
    const minZoom = spacecraftEntity.getOcclusionRadius() * 1.5;
    const maxZoom =
      (spacecraftEntity.getExtentsRadius() +
        compareEntity?.getExtentsRadius()) *
      20;
    zoomController.setDistanceClamp(new Pioneer.Interval(minZoom, maxZoom));
    //this._setupPickControllerPoI();
  }

  /**
   * Goes to the planets.
   * @private
   */

  async _goToPlanet(param = {}, name) {
    const { time } = param;
    const { queries, is2k, is4k } = appStore.stateSnapshot;
    const { hideUI } = queries;
    // console.log("inside go to planet--name ",name);
    // Make sure the entity is ready.
    await this._scene.waitTillEntitiesInPlace(new Set([name]));
    // const cameraEntity = this._scene.get('camera');
    const planet = this._scene.getEntity(name);

    const sun = this._scene.getEntity("sun");
    const cameraOrientation = new Pioneer.Quaternion();
    const dest = new Pioneer.Vector3();
    planet.getPositionRelativeToEntity(dest, Pioneer.Vector3.Zero, sun);
    dest.neg(dest);
    dest.normalize(dest);
    const planetUp = new Pioneer.Vector3();
    planet.getOrientation().getAxis(planetUp, 2);
    cameraOrientation.setFromAxes(undefined, dest, planetUp);
    let distance = Cameras.getDistanceToFitEntities(
      this._cameraEntity,
      cameraOrientation,
      planet,
      [planet],
    );
    const duration = this._cameraEntity.getParent() !== null ? 2.0 : 0.0;
    // console.log("planet ?", distance, planet)  //neptune = 152113.10176137617, pluto = NaN
    //TODO this distance should probably be different for differnt sized planets, super wrong for small planets  mercury
    const distanceScale = (hideUI === "true" && is2k) || is4k ? 1.6 : 1.3;
    if (Number.isFinite(distance)) {
      distance *= distanceScale; // Add some margin
    } else {
      const bodyRadius = Math.max(
        planet.getExtentsRadius(),
        planet.getOcclusionRadius(),
        1,
      );
      const parent = planet.getParent();
      const parentRadius = parent
        ? Math.max(parent.getExtentsRadius(), parent.getOcclusionRadius(), 0)
        : 0;
      distance =
        Math.max(10000, bodyRadius * 8, parentRadius * 0.5) * distanceScale;
    }

    const position = new Pioneer.Vector3();

    this._cameraEntity.getPositionRelativeToEntity(
      position,
      Pioneer.Vector3.Zero,
      planet,
    );

    if (this._previousEntity !== null) {
      // Look at the entity.
      const destination = new Pioneer.Vector3();
      this._cameraEntity.getPositionRelativeToEntity(
        destination,
        Pioneer.Vector3.Zero,
        planet,
      );

      await Cameras.goToEntity(this._cameraEntity, planet, {
        duration: duration / 6.0,
        up: true,
        distance: destination.magnitude(),
      });
    }
    // console.log("dest", dest, distance, position)
    dest.mult(dest, distance);
    const options = {
      up: true,
      fixedToParent: true,
      destination: dest,
      transitionFunction: Transitions.jumpToLocationOnSphere.bind(
        undefined,
        5,
        planet.getExtentsRadius(),
        true,
        planet,
      ),
    };

    if (time >= 0) {
      options.duration = time;
    }

    if (position.magnitude() > 0) {
      position.normalize(position);
      position.mult(position, distance);
      options.destination = position;
    }

    // check options here
    await Cameras.goToEntity(this._cameraEntity, planet, options);

    // Add zoom limits to the camera.
    const zoomController = this._cameraEntity.getController(
      Pioneer.ZoomController,
    );
    if (zoomController instanceof Pioneer.ZoomController) {
      const bodyRadius = Math.max(
        planet.getExtentsRadius(),
        planet.getOcclusionRadius(),
      );
      const modelMoonMinZoom = {
        phobos: 15,
        deimos: 10,
        mimas: 260,
        styx: 7,
        kerberos: 10,
        nix: 30,
        hydra: 30,
      };

      const minZoomDistance =
        modelMoonMinZoom[name] ??
        Math.max(0.1, Math.min(100, bodyRadius * 0.1));

      zoomController.setDistanceClamp(
        new Pioneer.Interval(minZoomDistance, Number.MAX_SAFE_INTEGER),
      );
    }

    // Spin controller.
    const align = this._cameraEntity.getController(Pioneer.AlignController);
    const spin = this._cameraEntity.addController(
      Pioneer.SpinController,
      undefined,
      align,
    );
    if (spin instanceof Pioneer.SpinController) {
      spin.setAxis(Pioneer.Vector3.ZAxis, true);
      spin.setRate(0.01);
      spin.setUsingRealTime(true);
      spin.setRotatingPosition(true);
    }
    // Tap controller.
    const tap = this._cameraEntity.addController(Pioneer.TapController);
    if (tap instanceof Pioneer.TapController) {
      tap.setTapCallback(() => {
        this._cameraEntity.removeController(spin);
        this._cameraEntity.removeController(tap);
      });
    }

    // Make the camera smoother when zoomed in.
    const orbitController = this._cameraEntity.getController(
      Pioneer.OrbitController,
    );
  }
  ChangePlanetTexture_HD = (planetName, isHD) => {
    const planet = this._scene.getEntity(planetName);
    const planetSpheroid = planet.getComponent(Pioneer.SpheroidLODComponent);
    if (planetName === "venus") {
      if (isHD) {
        planetSpheroid.setTexture(
          "color",
          "$STATIC_ASSETS_URL/maps/venus/surface_$SIZE_$FACE.png",
          [512],
        );
      } else {
        planetSpheroid.setTexture(
          "color",
          "$STATIC_ASSETS_URL/maps/venus/color_$SIZE_$FACE.png",
          [512],
        );
      }
    } else {
      showLoading("texture");
      if (isHD) {
        planetSpheroid.setTexture(
          "color",
          planetSpheroid.getTextureUrl("color"),
          [4096],
        );
      } else {
        planetSpheroid.setTexture(
          "color",
          planetSpheroid.getTextureUrl("color"),
          [512],
        );
      }
      new Promise((resolve) =>
        planetSpheroid.getLoadedPromise().then(() => {
          setTimeout(() => {
            resolve();
            hideLoading("texture");
          }, 500);
        }),
      );
    }
  };
  ResetPlanetTexture = (planetName, isMoon) => {
    if (!planetName || isMoon) return;
    const planet = this._scene.getEntity(planetName);
    const planetSpheroid = planet.getComponent(Pioneer.SpheroidLODComponent);
    const texture_arr = ["neptune", "uranus"].includes(planetName) ? 256 : 512;
    const color_texture = planetSpheroid.getTextureUrl("color");
    if (color_texture) {
      //not all "planets" have color textures
      planetSpheroid.setTexture("color", color_texture, [texture_arr]);
    }
  };
  /**
   * Goes to the earth.
   * @private
   */
  async _goToEarth(param = {}) {
    const {
      latitude,
      longitude,
      time,
      isGeostationary,
      resetCameraAngle,
      distance: customDistance,
      earthquakes,
    } = param;
    const { queries, is2k, is4k } = appStore.stateSnapshot;
    const { hideUI } = queries;

    // Make sure the entity is ready.
    await this._scene.waitTillEntitiesInPlace(["earth"]);

    const earth = this._scene.getEntity("earth");
    const sun = this._scene.getEntity("sun");

    const cameraOrientation = new Pioneer.Quaternion();
    const dest = new Pioneer.Vector3();
    earth.getPositionRelativeToEntity(dest, Pioneer.Vector3.Zero, sun);
    dest.neg(dest);
    dest.normalize(dest);
    const planetUp = new Pioneer.Vector3();
    earth.getOrientation().getAxis(planetUp, 2);
    cameraOrientation.setFromAxes(undefined, dest, planetUp);
    let distance = Cameras.getDistanceToFitEntities(
      this._cameraEntity,
      cameraOrientation,
      earth,
      [earth],
    );
    const duration = this._cameraEntity.getParent() !== null ? 2.0 : 0.0;

    // Zoom camera out if 2k and hideUI
    distance *= (hideUI === "true" && is2k) || is4k ? 1.6 : 1.3; // Add some margin
    if (customDistance) {
      distance = customDistance;
    }
    const position =
      resetCameraAngle === true ? this.startingCamPos : new Pioneer.Vector3();

    if (latitude && longitude) {
      const latlon = new Pioneer.LatLonAlt(
        Pioneer.MathUtils.degToRad(latitude),
        Pioneer.MathUtils.degToRad(longitude),
        0,
      );
      SceneHelpers.llaToXYZ(this.startingCamPos, earth, latlon, false);
      position.copy(this.startingCamPos);
    } else if (resetCameraAngle === false) {
      this._cameraEntity.getPositionRelativeToEntity(
        position,
        Pioneer.Vector3.Zero,
        earth,
      );
    }

    if (isGeostationary) {
      distance *= 5;
      position.addMult(position.y, position.z, 0.66);
    }

    if (this._previousEntity !== null) {
      // Look at the entity.
      const destination = new Pioneer.Vector3();
      this._cameraEntity.getPositionRelativeToEntity(
        destination,
        Pioneer.Vector3.Zero,
        earth,
      );
      await Cameras.goToEntity(this._cameraEntity, earth, {
        duration: duration / 6.0,
        up: true,
        distance: destination.magnitude(),
      });
    }

    dest.mult(dest, distance);
    const options = {
      up: true,
      fixedToParent: false,
      destination: dest,
      transitionFunction: Transitions.jumpToLocationOnSphere.bind(
        undefined,
        5,
        earth.getExtentsRadius(),
        true,
        earth,
      ),
    };

    if (time >= 0) {
      options.duration = time;
    }

    if (position.magnitude() > 0) {
      position.normalize(position);
      position.mult(position, distance);
      options.destination = position;
    }

    // check options here
    await Cameras.goToEntity(this._cameraEntity, earth, options);

    // Add zoom limits to the camera.
    const zoomController = this._cameraEntity.getController(
      Pioneer.ZoomController,
    );
    if (zoomController instanceof Pioneer.ZoomController) {
      const { currentDataset } = datasetStore.stateSnapshot;
      const { externalId, cameraZoom = 100 } = currentDataset || {};
      // adjust zoom to one for NISAR dataset
      zoomController.setDistanceClamp(
        new Pioneer.Interval(cameraZoom, Number.MAX_SAFE_INTEGER),
      );
    }

    // Spin controller.
    const align = this._cameraEntity.getController(Pioneer.AlignController);
    const spin = this._cameraEntity.addController(
      Pioneer.SpinController,
      undefined,
      align,
    );
    if (spin instanceof Pioneer.SpinController) {
      spin.setAxis(Pioneer.Vector3.ZAxis, true);
      spin.setRate(-0.01);
      spin.setUsingRealTime(true);
      spin.setRotatingPosition(true);
    }
    // Tap controller.
    const tap = this._cameraEntity.addController(Pioneer.TapController);
    if (tap instanceof Pioneer.TapController) {
      tap.setTapCallback(() => {
        this._cameraEntity.removeController(spin);
        this._cameraEntity.removeController(tap);
      });
    }

    // Make the camera smoother when zoomed in.
    const orbitController = this._scene
      .getEntity("camera")
      .getController(Pioneer.OrbitController);
  }

  setSpinRate(val) {
    const spin = this._cameraEntity.getController(Pioneer.SpinController);
    if (spin) {
      spin.setRate(val);
    }
  }

  /**
   * Goes to a spacecraft.
   * @param {string} name
   * @private
   */
  async _goToSpacecraft(name) {
    // Show loading.
    showLoading("model");

    // Make sure the entity is ready.
    //MERGE: This code is pretty different from ours. Will need testing and verification of changes
    await this._scene.waitTillEntitiesInPlace([name, "earth"]);

    // Make sure the entity's model is loaded. We force it since it isn't yet in view.
    const entity = this._scene.getEntity(name);
    let model = entity.getComponent(Pioneer.ModelComponent);
    let isInstrument = false;
    if (!model) {
      // Try the parent, since this entity may be an instrument.
      const parentEntity = entity.getParent();
      model = parentEntity.getComponent(Pioneer.ModelComponent);
      isInstrument = true;
    }
    if (model) {
      model.setForceLoaded(true);
      await model.getLoadedPromise(model);
    }

    // Hide loading.
    hideLoading("model");

    // Position the camera to have a nice view of the entity.
    const destination = new Pioneer.Vector3();
    const destinationUp = new Pioneer.Vector3();
    const destinationX = new Pioneer.Vector3();
    const destinationY = new Pioneer.Vector3();
    const destinationZ = new Pioneer.Vector3();
    destinationZ.normalize(entity.getPosition());
    destinationY.setNormalTo(destinationZ, entity.getVelocity());
    destinationY.neg(destinationY);
    destinationX.cross(destinationZ, destinationY);
    const furtherOutEntity =
      entity.getPosition().magnitude() >
      entity.getParent().getExtentsRadius() * 1.5;
    if (furtherOutEntity) {
      destination.addMult(destinationZ, destinationX, 0.05);
    } else {
      destination.addMult(destinationY, destinationZ, 0.66);
      destination.addMult(destination, destinationX, 0.66);
    }
    const cameraOrientation = this._cameraEntity.getOrientation().isNaN()
      ? Pioneer.Quaternion.Identity
      : this._cameraEntity.getOrientation();
    const distance = Cameras.getDistanceToFitEntities(
      this._cameraEntity,
      cameraOrientation,
      entity,
      [entity],
    );
    // console.log("camera_manager::_goToSpacecraft", {entity})

    // Check if we're flying around the earth.
    const lcaEntity = entity.getLowestCommonAncestor(this._previousEntity);
    if (lcaEntity && lcaEntity.getName() === "earth") {
      destination.setMagnitude(destination, distance * 10);

      // Get the up to be the current camera's "north".
      const latLonAlt = new Pioneer.LatLonAlt();
      SceneHelpers.xyzToLLA(latLonAlt, lcaEntity, entity.getPosition(), false);
      const spheroid = lcaEntity.getComponent(Pioneer.SpheroidComponent);
      spheroid.northFromLLA(destinationUp, latLonAlt);

      // Do the flight around the earth.
      await Cameras.goToEntity(this._cameraEntity, entity, {
        destination,
        destinationUp,
        up: true,
        orbiter: true,
        fixedToParent: false,
        duration: 1.0,
        transitionFunction: Transitions.jumpToLocationOnSphere.bind(
          undefined,
          5,
          lcaEntity.getExtentsRadius(),
          true,
          lcaEntity,
        ),
      });
    }

    // Set the destination to be a bit closer.
    if (name === "sc_iss_emit") {
      destination.copy(Pioneer.Vector3.YAxisNeg); // If it's EMIT, we need a special position to get it right.
    }
    destination.setMagnitude(destination, distance * 1.5);
    destination.rotateInverse(entity.getOrientation(), destination);

    // Look at and then fly to the final destination.
    await Cameras.lookAtEntity(this._cameraEntity, entity, {
      duration: 0.25,
    });
    await Cameras.goToEntity(this._cameraEntity, entity, {
      destination,
      destinationInFocusFrame: true,
      orbiter: !isInstrument,
      fixedToParent: true,
      duration: 1.0,
      transitionFunction: easeTransition,
    });

    // Turn off the force loading that was enabled above.
    if (model) {
      model.setForceLoaded(false);
    }

    // Zoom clamping
    const minRadius = 1.2 * entity.getOcclusionRadius();
    let maxRadius = 10 * entity.getExtentsRadius();
    if (isInstrument) {
      maxRadius = 3 * entity.getParent().getExtentsRadius();
    }
    this._cameraEntity
      .getController(Pioneer.ZoomController)
      .setDistanceClamp(new Pioneer.Interval(minRadius, maxRadius));

    // Set some angle limits so it doesn't spaz at the poles.
    this._cameraEntity
      .getController(Pioneer.OrbitController)
      .setPitchAngleLimits(
        new Pioneer.Interval(-Math.PI / 2 + 0.01, Math.PI / 2 - 0.01),
      );
  }

  /**
   * Goes to a video patch.
   * @param {object} videoOrEvent
   */
  async goToPatch(videoOrEvent) {
    // console.log(videoOrEvent); //Todo: methane plume event
    const {
      urlParam,
      id,
      boundaries: { north, south, east, west },
      distance_from_event,
      zoom_factor,
    } = videoOrEvent;
    const earth = this._scene.getEntity("earth");
    const placemarkId = `event_patch_${urlParam || id}`;
    let placemarkEntity = this._scene.getEntity(placemarkId);
    const lat = (south + north) / 2;
    const lon = (west + east) / 2;
    if (!placemarkEntity) {
      placemarkEntity = Placemarks.addPlacemark(
        placemarkId,
        "",
        earth,
        lat,
        lon,
        0,
      );
    }
    let zoomFactor = zoom_factor || 1.0;
    let distanceFromEventFixed = distance_from_event || 20;
    // await this.goToDataPatchWithZoom(north, south, east, west, placemarkEntity, distanceFromEventFixed, zoomFactor);
    await this.goToDataPatch(
      north,
      south,
      east,
      west,
      placemarkEntity,
      distanceFromEventFixed,
    );
  }

  /**
   * Goes to a data patch.
   * @param {any} poi
   */
  async goToPoiPatch(poi) {
    const {
      north_boundary,
      south_boundary,
      east_boundary,
      west_boundary,
      id,
      distance_from_poi,
    } = {
      ...poi,
      north_boundary: parseFloat(poi.north_boundary),
      south_boundary: parseFloat(poi.south_boundary),
      east_boundary: parseFloat(poi.east_boundary),
      west_boundary: parseFloat(poi.west_boundary),
    };

    const earth = this._scene.getEntity("earth");
    // Frame the image bounds rather than the list marker's coordinates.
    const placemarkId = "poi_patch_camera_" + id;
    let placemarkEntityPoI = this._scene.getEntity(placemarkId);
    if (!placemarkEntityPoI) {
      const lat = (south_boundary + north_boundary) / 2;
      const lon = (west_boundary + east_boundary) / 2;
      placemarkEntityPoI = Placemarks.addPlacemark(
        placemarkId,
        "",
        earth,
        lat,
        lon,
        0,
      );
    }

    await this.goToDataPatch(
      north_boundary,
      south_boundary,
      east_boundary,
      west_boundary,
      placemarkEntityPoI,
      distance_from_poi,
    );
  }

  /**
   * Goes to a data patch.
   * @param {any} gmap
   */
  async goToGmapPatch(gmap) {
    const {
      north_boundary,
      south_boundary,
      east_boundary,
      west_boundary,
      id,
      distance_from_gmap,
    } = gmap;
    const earth = this._scene.get("earth");
    let placemarkEntityGmap = this._scene.get("gmap_" + id);
    if (!placemarkEntityGmap) {
      const lat = (south_boundary + north_boundary) / 2;
      const lon = (west_boundary + east_boundary) / 2;
      placemarkEntityGmap = Placemarks.addPlacemark(
        "gmap_" + id,
        "",
        earth,
        lat,
        lon,
        0,
      );
    }

    await this.goToDataPatch(
      north_boundary,
      south_boundary,
      east_boundary,
      west_boundary,
      placemarkEntityGmap,
      distance_from_gmap,
    );
  }

  /**
   * Goes to a NISAR data patch.
   * @param {any} nisar
   */
  async goToNisarPatch(nisar) {
    const { north_boundary, south_boundary, east_boundary, west_boundary, id } =
      {
        ...nisar,
        north_boundary: parseFloat(nisar.north_boundary),
        south_boundary: parseFloat(nisar.south_boundary),
        east_boundary: parseFloat(nisar.east_boundary),
        west_boundary: parseFloat(nisar.west_boundary),
      };
    const earth = this._scene.getEntity("earth");
    const placemarkId = "nisar_" + id;
    let placemarkEntityNisar = this._scene.getEntity(placemarkId);

    if (!placemarkEntityNisar) {
      const lat = (south_boundary + north_boundary) / 2;
      const lon = (west_boundary + east_boundary) / 2;
      placemarkEntityNisar = Placemarks.addPlacemark(
        placemarkId,
        "",
        earth,
        lat,
        lon,
        0,
      );
    }

    await this.goToDataPatch(
      north_boundary,
      south_boundary,
      east_boundary,
      west_boundary,
      placemarkEntityNisar,
      // Use the standard Events framing and zoom limits, independent of metadata.
      20,
    );
  }

  /**
   * Goes to a data patch either event or video.
   * @param {*} northBoundary
   * @param {*} southBoundary
   * @param {*} patchEntity
   */
  async goToDataPatch(
    northBoundary,
    southBoundary,
    eastBoundary,
    westBoundary,
    placemarkEntity,
    distance = 5,
    framingMultiplier,
  ) {
    placemarkEntity.setEnabled(true);
    // Disabling the element is hacky and should be fixed with better state management / element organization.
    placemarkEntity.getComponent(Pioneer.DivComponent).setEnabled(false);
    const camera = this._scene
      .getEntity("camera")
      .getComponent(Pioneer.CameraComponent);
    if (!(camera instanceof Pioneer.CameraComponent)) {
      throw new Error();
    }

    lightStore.setGlobalState({ fill: true });

    const earth = this._scene.getEntity("earth");

    // set the mid distance for mobile powerscaling
    camera.setMidDistance(120000);

    if (this._cameraEntity.getParent() !== null) {
      // Enable the eventPlacemark so the camera can transtion
      this._cameraEntity.getParent().setEnabled(true);
    }

    // Get some good lat and lon distances so that event looks good on the screen.
    const latAngularDistance = Pioneer.MathUtils.degToRad(
      northBoundary - southBoundary,
    );
    const lonAngularDistance = Pioneer.MathUtils.degToRad(
      eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0),
    );
    const dist = Math.min(distance, 20);
    const distanceMod = framingMultiplier ?? (dist === 20 ? 0.5 : dist * dist);
    const latFOVFactor =
      1.0 /
      Math.atan(
        this._scene
          .getEntity("camera")
          .getComponent(Pioneer.CameraComponent)
          .getVerticalFieldOfView() / 2.0,
      );
    const lonFOVFactor =
      1.0 /
      Math.atan(
        this._scene
          .getEntity("camera")
          .getComponent(Pioneer.CameraComponent)
          .getHorizontalFieldOfView() / 2.0,
      );
    const latDistance =
      latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
    const lonDistance =
      lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
    const distanceFromEvent =
      Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) *
      distanceMod;

    // console.log("distance details: ", {
    //   dist,
    //   distance,
    //   latDistance,
    //   lonDistance,
    //   distanceFromEvent,
    //   latFOVFactor,
    //   lonFOVFactor,
    //   distanceMod,
    //   latAngularDistance,
    //   lonAngularDistance,
    // });

    // Wait until the placemark and the earth has a valid position and orientation.
    await this._scene.waitTillEntitiesInPlace([
      "earth",
      placemarkEntity.getName(),
    ]);
    // Position camera destination to be above the event.
    const eventPosition = placemarkEntity.getPosition();
    const destination = new Pioneer.Vector3();
    destination.normalize(eventPosition);
    destination.mult(destination, distanceFromEvent);

    try {
      await Cameras.goToEntity(this._cameraEntity, placemarkEntity, {
        destination,
        fixedToParent: true,
        up: false,
        duration: 3,
        transitionFunction: Transitions.jumpToLocationOnSphere.bind(
          undefined,
          5,
          earth.getExtentsRadius(),
          true,
          earth,
        ),
      });
    } catch (error) {
      if (isInterruptedTransitionError(error)) {
        return;
      }

      throw error;
    }

    // Make the camera align to the placemark's north-east orientation.
    const alignController = this._scene
      .getEntity("camera")
      .getController(Pioneer.AlignController);
    if (!(alignController instanceof Pioneer.AlignController)) {
      throw new Error();
    }
    alignController.setSecondaryAlignType("align");
    alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
    alignController.setSecondaryTargetEntity(placemarkEntity.getName());
    alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
    // Make the camera orbit the placemark with nice limits on its axes.
    const orbitController = this._scene
      .getEntity("camera")
      .getController(Pioneer.OrbitController);
    if (!(orbitController instanceof Pioneer.OrbitController)) {
      throw new Error();
    }
    orbitController.setYawAxisType("y-axis");
    orbitController.setPitchAxisType("z-axis");
    orbitController.setYawAngleLimits(
      new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4),
    );
    orbitController.setPitchAngleLimits(
      new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4),
    );
    // Add zoom limits to the camera.
    const zoomController = this._scene
      .getEntity("camera")
      .getController(Pioneer.ZoomController);
    if (!(zoomController instanceof Pioneer.ZoomController)) {
      throw new Error();
    }
    const minZoom = Math.min(distanceFromEvent * 0.4, 5);

    zoomController.setDistanceClamp(
      new Pioneer.Interval(minZoom, earth.getExtentsRadius() * 3),
    );
  }
  /**
   * Goes to a data patch either poi or video.
   * @param {*} northBoundary
   * @param {*} southBoundary
   * @param {*} patchEntity
   */
  //  async goToDataPatchWithZoom(northBoundary, southBoundary, eastBoundary, westBoundary, placemarkEntity,distanceFromPoiFixed,zoomFactorPoi) {
  //   placemarkEntity.setEnabled(true);
  // 	// Disabling the element is hacky and should be fixed with better state management / element organization.
  //   placemarkEntity.getComponent(Pioneer.DivComponent).setEnabled(false)
  //   const camera = this._scene.getEntity('camera').getComponent(Pioneer.CameraComponent);
  //   if (!(camera instanceof Pioneer.CameraComponent)) {
  // 		throw new Error();
  // 	}

  // 	lightStore.setGlobalState({ fill: true });

  //   const earth = this._scene.getEntity('earth');

  // 	// set the mid distance for mobile powerscaling
  // 	camera.setMidDistance(120000);

  // 	if (this._cameraEntity.getParent() !== null) {
  // 		// Enable the eventPlacemark so the camera can transtion
  // 		this._cameraEntity.getParent().setEnabled(true);
  // 	}

  // 	// Get some good lat and lon distances so that event looks good on the screen.
  // 	const latAngularDistance = Pioneer.MathUtils.degToRad(northBoundary - southBoundary);
  // 	const lonAngularDistance = Pioneer.MathUtils.degToRad(eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0));
  //   const latFOVFactor = 1.0 / Math.atan(this._scene.getEntity('camera').getComponent(Pioneer.CameraComponent).getVerticalFieldOfView() / 2.0);
  //   const lonFOVFactor = 1.0 / Math.atan(this._scene.getEntity('camera').getComponent(Pioneer.CameraComponent).getHorizontalFieldOfView() / 2.0);
  // 	const latDistance = latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
  // 	const lonDistance = lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
  // 	const distanceFromEvent = Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) * 0.5;

  // 	// Wait until the placemark and the earth has a valid position and orientation.
  // 	await this._scene.waitTillEntitiesInPlace(['earth', placemarkEntity.getName()]);
  //   console.log("goToDataPatchWithZoom")

  // 	// Position camera destination to be above the event.
  // 	const eventPosition = placemarkEntity.getPosition();
  // 	const destination = new Pioneer.Vector3();
  // 	destination.normalize(eventPosition);
  // 	destination.mult(destination, distanceFromEvent);

  // 	await Cameras.goToEntity(this._cameraEntity, placemarkEntity, {
  // 		destination,
  // 		fixedToParent: true,
  // 		up: false,
  // 		duration: 3,
  // 		transitionFunction: Transitions.jumpToLocationOnSphere.bind(0, 5, earth.getExtentsRadius(), true, earth)
  // 	});

  // 	// Make the camera align to the placemark's north-east orientation.
  // 	const alignController = this._scene.getEntity('camera').getController(Pioneer.AlignController);
  // 	if (!(alignController instanceof Pioneer.AlignController)) {
  // 		throw new Error();
  // 	}
  // 	alignController.setSecondaryAlignType('align');
  // 	alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
  // 	alignController.setSecondaryTargetEntity(placemarkEntity.getName());
  // 	alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
  // 	// Make the camera orbit the placemark with nice limits on its axes.
  // 	const orbitController = this._scene.getEntity('camera').getController(Pioneer.OrbitController);
  // 	if (!(orbitController instanceof Pioneer.OrbitController)) {
  // 		throw new Error();
  // 	}
  // 	orbitController.setYawAxisType('y-axis');
  // 	orbitController.setPitchAxisType('z-axis');
  // 	orbitController.setYawAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
  // 	orbitController.setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
  // 	// Add zoom limits to the camera.
  // 	const zoomController = this._scene.getEntity('camera').getController(Pioneer.ZoomController);
  // 	if (!(zoomController instanceof Pioneer.ZoomController)) {
  // 		throw new Error();
  // 	}
  // 	zoomController.setDistanceClamp(new Pioneer.Interval(distanceFromPoiFixed, earth.getExtentsRadius() * 3));
  // }

  /**
   * Goes to a data patch either gmap or video.
   * @param {*} northBoundary
   * @param {*} southBoundary
   * @param {*} patchEntity
   */
  //   async goToDataPatchGmap(northBoundary, southBoundary, eastBoundary, westBoundary, placemarkEntityGmap,distanceFromGmapFixed,zoomFactorGmap) {
  // 	placemarkEntityGmap.setEnabled(true);
  // 	// Disabling the element is hacky and should be fixed with better state management / element organization.
  // 	placemarkEntityGmap.get('div').setEnabled(false);
  // 	const camera = this._scene.get('camera', 'camera');
  // 	if (!(camera instanceof Pioneer.CameraComponent)) {
  // 		throw new Error();
  // 	}

  // 	const cameraEntity = this._scene.get('camera');
  // 	const earth = this._scene.get('earth');
  // 	earth.get('atmosphere').setEnabled(false);

  // 	// set the mid distance for mobile powerscaling
  // 	camera.setMidDistance(120000);
  // 	// Get some good lat and lon distances so that event looks good on the screen.
  // 	const latAngularDistance = Pioneer.MathUtils.degToRad(northBoundary - southBoundary);
  // 	const lonAngularDistance = Pioneer.MathUtils.degToRad(eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0));

  // 	const latFOVFactor = zoomFactorGmap / Math.atan(this._scene.get('camera', 'camera').getVerticalFieldOfView() / 2.0);
  // 	const lonFOVFactor = zoomFactorGmap / Math.atan(this._scene.get('camera', 'camera').getHorizontalFieldOfView() / 2.0);

  // 	const latDistance = latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
  // 	const lonDistance = lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
  // 	const distanceFromGmap = Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) * 0.5;
  // 	// Wait until the placemark and the earth has a valid position and orientation.
  // 	await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set([placemarkEntityGmap.getName(), 'earth']));
  // 	// Position camera destination to be above the gmap.
  // 	const gmapPosition = placemarkEntityGmap.getPosition();
  // 	const destination = new Pioneer.Vector3();
  // 	destination.normalize(gmapPosition);
  // 	destination.mult(destination, distanceFromGmap);
  // 	await Cameras.goToEntity(cameraEntity, placemarkEntityGmap, {
  // 		destination: destination,
  // 		fixedToParent: true,
  // 		up: false,
  // 		duration: 3,
  // 		transitionFunction: Transitions.jumpToLocationOnSphere.bind(0, 5, earth.getExtentsRadius(), true, earth)
  // 	});
  // 	// Make the camera align to the placemark's north-east orientation.
  // 	const alignController = this._scene.get('camera', 'align');
  // 	if (!(alignController instanceof Pioneer.AlignController)) {
  // 		throw new Error();
  // 	}
  // 	alignController.setSecondaryAlignType('align');
  // 	alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
  // 	alignController.setSecondaryTargetEntity(placemarkEntityGmap);
  // 	alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
  // 	// Make the camera orbit the placemark with nice limits on its axes.
  // 	const orbitController = this._scene.get('camera', 'orbit');
  // 	if (!(orbitController instanceof Pioneer.OrbitController)) {
  // 		throw new Error();
  // 	}
  // 	orbitController.setYawAxisType('y-axis');
  // 	orbitController.setPitchAxisType('z-axis');
  // 	orbitController.setYawAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
  // 	orbitController.setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
  // 	// Add zoom limits to the camera.
  // 	const zoomController = this._scene.get('camera', 'zoom');
  // 	if (!(zoomController instanceof Pioneer.ZoomController)) {
  // 		throw new Error();
  // 	}

  // 		zoomController.setDistanceClamp(new Pioneer.Interval(distanceFromGmapFixed, earth.getExtentsRadius() * 3));
  // }

  /**
   * Offsets Pioneer Viewport Left by percent.
   * Used to offset globe during desktop charts and modal videos.
   * Additionally it offsets the dateString and colorbar elements to match the globe.
   * @param {number} percent
   */
  offsetPioneerViewportLeft(percent) {
    const root = document.getElementById("root");

    // Set CSS variable on root element.
    root.style.setProperty("--viewport-offset", percent.toString());
  }
  /**
   * Sets the pick controller.
   * @private
   */
  setPickController() {
    const pickController =
      this._cameraEntity.getController(Pioneer.PickController) ??
      this._cameraEntity.addController(Pioneer.PickController);
    const earth = this._scene.getEntity("earth");
    pickController.setPickedEntity(earth);
    pickController.setTriggerOnHover(false);

    pickController.setCallback((position) =>
      this._calcDataFromPosition(position),
    );
  }

  /**
   * Unsets the pick controller.
   */
  unsetPickController() {
    const pickController = this._cameraEntity.getController(
      Pioneer.PickController,
    );
    pickController?.setCallback(null);
  }

  /**
   * - Inputs an xyz position vector,
   * - Applies latlong conversions to allow it to sample the value from the same spot on the 2d canvas map
   * - Uses readoutData values to calculate data values and thumb percentage
   * - Sets them if valid
   * @param {Vector3} position
   */
  _calcDataFromPosition(position, forRegion = false) {
    const { getManager } = globalRefs;
    const { datasetHasAnimation, isDatasetPaused, currentDataset } =
      datasetStore.stateSnapshot;
    const { readoutData, externalId } = currentDataset || {};

    const allowSample =
      (forRegion || !datasetHasAnimation || isDatasetPaused) &&
      readoutData &&
      Number.isFinite(position?.x) &&
      Number.isFinite(position?.y);

    // Return if sample not allowed.
    if (!allowSample) {
      return;
    }

    const earth = this._scene.getEntity("earth");

    // Format latitude and longitude
    const xy = Pioneer.Vector2.pool.get();
    const lla = Pioneer.LatLonAlt.pool.get();
    const positionInFrame = Pioneer.Vector3.pool.get();
    positionInFrame.rotateInverse(earth.getOrientation(), position);
    earth
      .getComponent(Pioneer.SpheroidComponent)
      .llaFromXYZ(lla, positionInFrame);

    xy.x = ((Pioneer.MathUtils.radToDeg(lla.lon) + 180.0) / 360) * 1440;

    // console.log('lla.lon', Pioneer.MathUtils.radToDeg(lla.lon), lla.lon)
    xy.y = (1 - (Pioneer.MathUtils.radToDeg(lla.lat) + 90.0) / 180) * 720;

    // console.log("_calcDataFromPosition values", {xy, positionInFrame, lla, position} )
    const data_vals =
      getManager("dataset").getValueFromCanvas(xy.x, xy.y) || [];
    const [dataR, dataG, dataB] = data_vals;
    const noData = dataR <= 1 && dataG <= 1 && dataB <= 1;
    let result;
    const possibleTileManager = getManager("dataset").tileManager;
    const isWMTS = Boolean(possibleTileManager);
    const isWMTSCubemap = Boolean(possibleTileManager?.isCubemap);
    const validNoData = !isWMTS || isWMTSCubemap;

    // If the first data item is 1, we've sampled a black area. Release the vector and return early.
    if (noData && validNoData) {
      result = null;
      Pioneer.LatLonAlt.pool.release(lla);
      Pioneer.Vector2.pool.release(xy);
      Pioneer.Vector3.pool.release(positionInFrame);
      return;
    } else {
      result = this._getDataValuesFromRgbAndSetReadoutAndColorbarIndicator(
        dataR,
        dataG,
        dataB,
        lla.lat,
        lla.lon,
        currentDataset,
        isWMTSCubemap ? null : possibleTileManager,
      );
      Pioneer.LatLonAlt.pool.release(lla);
      Pioneer.Vector2.pool.release(xy);
      Pioneer.Vector3.pool.release(positionInFrame);
    }
    return result;
  }

  //This thing is doing too much. Should refactor
  _getDataValuesFromRgbAndSetReadoutAndColorbarIndicator(
    dataR,
    dataG,
    dataB,
    lat,
    lon,
    currentDataset,
    possibleTileManager,
  ) {
    const { pioneer } = globalRefs;
    const { isCelcius } = datareadStore.stateSnapshot;
    const { readoutData, externalId } = currentDataset || {};
    const soilMoistAndSal = currentDataset.externalId === "smapSmSalinity8Day";
    const precipitation = currentDataset.externalId === "precipitationToday";

    // Get stored cursor position.
    const cursorPosition = pioneer.getInput().getCursorPosition();

    // Unique case: If precipitation data, apply individual calculation and rect style.
    if (precipitation) {
      const { rain, snow } = readoutData;
      const { scalingFactor: rainScale, units: rainUnits } = rain;
      const { scalingFactor: snowScale, units: snowUnits } = snow;

      // Specific precipitation equations contact: Jeff Hall.
      const readValue = dataG + 256 * dataB;
      const isSnow = dataR === 2;

      const scale = isSnow ? snowScale : rainScale;
      let units = isSnow ? snowUnits : rainUnits;

      const dataValue = readValue * scale;

      if (possibleTileManager) {
        dataValue = possibleTileManager.calculateDataFromColor();
        if (dataValue === null) {
          return;
        }
      }

      // console.log("camera_manager._calcDataFromPosition", dataValue)

      // Format the value without units so RTL rendering stays correct
      const valueString = `${formatNumber(dataValue, 2)}`;
      const dataUnit = ` ${units}`;
      let valueStringSave = valueString;
      // add latlon values for every vital sign value -zahrah
      let latlonx = Pioneer.MathUtils.radToDeg(lat);
      let latlony = Pioneer.MathUtils.radToDeg(lon);
      let decimalPlace = 2;
      let valueLatLonStringx = `${Math.abs(latlonx).toFixed(decimalPlace)} ° ${latlonx > 0 ? "N" : "S"}`;
      let valueLatLonStringy = `${Math.abs(latlony).toFixed(decimalPlace)} ° ${latlony > 0 ? "E" : "W"}`;
      const valueLatLonText =
        "" + valueLatLonStringx + "" + ", " + valueLatLonStringy;

      // add latlon values readout -zahrah
      setReadoutData(valueString, valueLatLonText, cursorPosition, dataUnit);
      // console.log("readout", valueString, valueLatLonText, this._app.getCursorPosition())

      // Get the nearest dataLookup index and pass to setPrecipitationColorRectStyle.
      const nearestIndex = getNearestDataIndex(dataValue);
      setPrecipitationColorRectStyle(nearestIndex, isSnow, this._pioneer);

      console.log("RETURNING DATA", {
        value: valueStringSave,
        units: units,
        lat: valueLatLonStringx,
        lon: valueLatLonStringy,
      });

      return {
        // valueString,
        // valueLatLonText,
        value: valueStringSave,
        units: units,
        lat: valueLatLonStringx,
        lon: valueLatLonStringy,
      };
    }

    // Get readout data vars.
    const { bottom, variation, numOffset, scalingFactor, units, dps } =
      readoutData;

    // Explorer equation - value = (int)(color.g * 255 * 255) + (int)(color.b * 255)) * vD.scalingFactor + vD.numOffset
    // Declare vars.
    let readValue = (dataB + 256 * dataG) * scalingFactor + numOffset;
    let percentScale = null;
    let dataUnits = units;
    let decimalPlaces = dps ?? 1;
    let isDoubleColorBar = false;
    let isUpper = true;

    if (possibleTileManager) {
      readValue = possibleTileManager.calculateDataFromColor();
      if (readValue === null) {
        return;
      }
    }

    // console.log("camera_manager._calcDataFromPosition", readValue)

    // Calculate % value before celsius conversion.
    if (!isNaN(readValue)) {
      percentScale = Pioneer.MathUtils.clamp(
        (readValue - bottom) / variation,
        0,
        1,
      ); // Values can exceed colorbar scale (GFO, SSHA, etc.)
    }

    if (soilMoistAndSal) {
      if (readValue >= 1001) {
        // NaN returns false
        // WATER - salinity
        dataUnits = "وحدة ملوحة عملية"; // is there somewhere that explains this?
        readValue *= 0.01;
        decimalPlaces = 1;
        percentScale = Pioneer.MathUtils.clamp((readValue - 30) / 10, 0, 1); // Range of 30-40 PSU
        isUpper = false;
      } else {
        // Land cm3/cm3
        readValue *= 0.001;
        percentScale = Pioneer.MathUtils.clamp((readValue - 0) / 0.6, 0, 1); // Range of 0.0 - 0.6 cm3/cm3
        isUpper = true;
      }

      // Trigger offsets for double color bar
      isDoubleColorBar = true;
    } else if (dataUnits === "م°" && !isCelcius) {
      // Convert to Fahrenheit to match color bar.
      dataUnits = "ف°";
      // Fahrenheit = Celsius * 1.8 + 32
      readValue = readValue * 1.8 + 32;
    } else if (dataUnits === "ف°" && isCelcius) {
      // Convert to Celsius to match color bar.
      //units = '°C';   // to change to arabic celsius
      dataUnits = "م°";
      // Celsius = (Fahrenheit - 32) / 1.8
      readValue = (readValue - 32) / 1.8;
    }

    // Validation.
    if (
      percentScale !== null &&
      !isNaN(percentScale) &&
      readValue !== null &&
      !isNaN(readValue)
    ) {
      const valueString = `${formatNumber(readValue, decimalPlaces)}`;
      const dataUnit = ` ${dataUnits}`;
      let valueStringSave = `${formatNumber(readValue, decimalPlaces)}`;
      // add latlon values for every vital sign value -zahrah
      let latlonx = Pioneer.MathUtils.radToDeg(lat);
      let latlony = Pioneer.MathUtils.radToDeg(lon);
      let decimalPlace = 2;
      let valueLatLonStringx = `${Math.abs(latlonx).toFixed(decimalPlace)} ° ${latlonx > 0 ? "N" : "S"}`;
      let valueLatLonStringy = `${Math.abs(latlony).toFixed(decimalPlace)} ° ${latlony > 0 ? "E" : "W"}`;
      const valueLatLonText =
        "" + valueLatLonStringx + "" + ", " + valueLatLonStringy;
      // Set colorbar value position and readout reading.
      // subtract from 1 to invert for rtl colorbar !! super important !!
      setColorRectStyle(1 - percentScale, isDoubleColorBar, isUpper);
      // add latlon values readout -zahrah
      // setReadoutData(valueString, valueLatLonText, cursorPosition);

      const { isCursorInsideSelection } = datareadStore.stateSnapshot;

      if (!isCursorInsideSelection) {
        setReadoutData(valueString, valueLatLonText, cursorPosition, dataUnit);
      }
      //console.log("readout", valueString, valueLatLonText, this._app.getCursorPosition())
      console.log("RETURNING DATA", {
        value: valueStringSave,
        units: dataUnits,
        lat: valueLatLonStringx,
        lon: valueLatLonStringy,
      });
      return {
        value: valueStringSave,
        units: dataUnits,
        lat: valueLatLonStringx,
        lon: valueLatLonStringy,
      };
    } else {
      console.error("bad read data", percentScale, readValue);
      return null;
    }
  }
}

export default CameraManager;
